<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-contact-reasons-detail-topic-anomalies-chart" v-loading="loading">
    <table border="0">
    <tr>
      <td width="*">
        <div class="title">{{chartData.title}}</div>
      </td>
      <td align="right" width="250">
        <div class="wy-chart-grouping">
          <el-select
            v-model="selectedDateRange"
            collapse-tags
            size="mini"
            style="margin-left: 5px;"
            popper-class="line-chart-dropdown"
            @change="changeDateRange"
            placeholder="Select range">
            <el-option
              v-for="(item) in dateRangeTypes"
              :disabled="item.disabled"
              :key="item.type"
              :label="item.label"
              :value="item.type">
            </el-option>
          </el-select>
        </div>
      </td>
    </tr>
    </table>
    <div ref="chart" :id="`chart-anomalies-${chartData.id}`" class="anomalies-chart" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import WyChartModule from '../../library/wy-chart-module.js';
import moment from 'moment';

export default Vue.extend({
  name: 'wy-analytics-contact-reasons-detail-topic-anomalies-chart',
  props: ['activeTab', 'chartData'],

  data() {
    return {
      loading: false,
      wyChart: null,
      selectedDateRange: null,
      dataList: null,
      anomalyList: null
    }
  },

  watch: {
    activeTab(type) {      
      if(type == 'anomalies') {
        this.buildChart();
      }
      else {
        this.$refs.chart.innerHTML = '';
      }
    },

    chartData(val) {
      let hasData = val != null;
      this.initData(this.selectedDateRange);
      if(hasData) {
        this.buildChart();
      }
    }
  },

  computed: {
    ...mapGetters(['dataModel']),

    dateRangeTypes() {
      return this.wyChart.dateRangeTypes;
    },
  },

  methods: {
    changeDateRange(type) {
      this.initData(type);
      this.buildChart();
    },

    buildChart() {
      if(this.dataList && this.dataList.length > 0) {
        this.loading = true;
        let dateRangeType = this.wyChart.getDateRangeType();        
        if(dateRangeType.type == 'week' || dateRangeType.type == 'day') {
          dateRangeType.tickformat = dateRangeType.tickformat.replace('Y', 'y');
        }

        let traceA = {dates: [], values: [], texts: []};
        let traceB = {dates: [], values: [], texts: [], markers: []};
        this.dataList.forEach(item => {
          // trade A
          traceA.dates.push(item.date);
          if(this.chartData.suffix == 'm') {
            traceA.values.push(item.value);
            traceA.texts.push(WyUtils.toNiceTimeFormatFromMinus(item.value));
          }
          else {
            traceA.values.push(item.value);
            traceA.texts.push(WyUtils.formatterNumber(item.value));
          }

          // trade B anomaly
          if(item.anomaly > 0) {
            traceB.dates.push(item.date);
            traceB.values.push(item.anomaly);
            if(this.chartData.suffix == 'm') {
              traceB.texts.push(WyUtils.toNiceTimeFormatFromMinus(item.anomaly));
            }
            else {
              traceB.texts.push(WyUtils.formatterNumber(item.anomaly));
            }
            traceB.markers.push(this.chartData.transcriptDate == item.date ? 12 : 8);            
          }
        })
       
        setTimeout(() => {
          let traces = [];

          traces.push({
            type: "scatter",
            mode: "lines+markers",
            line: {
              shape: 'spline',
              color: '#3FA9F5'
            },
            x: traceA.dates,
            y: traceA.values,
            hovertext: traceA.texts,
            hovertemplate: this.wyChart.hoverTemplate(this.chartData.suffix),
          });

          traces.push({
            type: "scatter",
            mode: "markers",            
            x: traceB.dates,
            y: traceB.values,
            hovertext: traceB.texts,
            hovertemplate: this.wyChart.hoverTemplate(this.chartData.suffix),
            marker: {
              size: traceB.markers,
              color: '#FF0000'
            }
          });

          let layout = {
            autosize: true,
            //width: 560,
            height: 240,          
            dragmode: false, //'pan'
            separators: '.',
            showlegend: false,
            plot_bgcolor: '#ffffff',
            paper_bgcolor:'transparent',
            hoverlabel: {
              namelength: 50,
              font: {
                size: 11
              },
              bordercolor: '#FCFEFF'
            },
            legend: {
              orientation: 'h',
              y: -0.2
            },
            margin: { l: 60, r: 35, t: 20, b: 30 },
              xaxis:  {
              type: 'date',
              tickformat: dateRangeType.tickformat,
              autorange: true,
              fixedrange: true,
              showgrid: true,
              zeroline: false,
              showline: true,
              linecolor: '#dedede',
              position: 0,
              tickcolor: "transparent"
            },
            yaxis: {
              type: 'linear',
              autorange: true,
              rangemode: 'tozero',
              separatethousands: true,
              showgrid: false,
              showline: true,
              zeroline: true,
              linecolor: '#dedede',
              ticksuffix: (this.chartData.suffix + ' '),
              tickformat: (WyUtils[this.chartData.format] ? '' : this.chartData.format),
              fixedrange: true,
            }
          }
          this.wyChart.finalizeChart(traces, layout);
          
          if(document.querySelector(`#chart-anomalies-${this.chartData.id}`)) {
            let config = {responsive: true, displayModeBar: false, scrollZoom: false};
            // eslint-disable-next-line no-undef
            Plotly.newPlot(`chart-anomalies-${this.chartData.id}`, traces, layout, config);
          }

          this.loading = false;
        }, 300)
      }
    },

    initData(type) {
      //this.generateMockData();
      
      if(this.chartData && this.chartData.data && this.chartData.data.trend) {
        let dataItems = this.chartData.data.trend;
      
        // prepare date range
        let dateStart = moment(dataItems[0].trendDate);
        let dateEnd = moment(dataItems[dataItems.length-1].trendDate);

        this.wyChart.init(type, dateStart, dateEnd);
        this.selectedDateRange = this.wyChart.selectedDateRange;        

        // prepare date types
        dataItems.forEach(el => {
          this.wyChart.prepareGroupDates(el, el.trendDate);
        });

        // group by type
        this.dataList = this.wyChart.groupBy(dataItems, (items) => {
          let date = items[0].day;
          // eslint-disable-next-line no-undef
          let sumConversations = _.sumBy(items, 'sumConversations');
          let value = 0;
          let anomaly = 0;
          
          if(this.chartData.suffix == 'm') {
            // eslint-disable-next-line no-undef
            value = _.sumBy(items, this.chartData.column) / sumConversations
            // eslint-disable-next-line no-undef
            anomaly = _.sumBy(items, 'anomaly') / items.length
          }
          else if(this.chartData.suffix == '%') {
            // eslint-disable-next-line no-undef
            value = _.sumBy(items, this.chartData.column) / items.length
            // eslint-disable-next-line no-undef
            anomaly = _.sumBy(items, 'anomaly') / items.length
          }
          else {
            // eslint-disable-next-line no-undef
            value = _.sumBy(items, this.chartData.column)
            // eslint-disable-next-line no-undef
            anomaly = _.sumBy(items, 'anomaly')
          }

          return {
            date: date,
            value: value,
            anomaly: anomaly
          }
        });
      }
    },

    generateMockData() {
      let today = moment()
      let trend = [];
      for(var i=1555; i > 0; i--) {
        let date = today.subtract(1, 'd')
        trend.push({
          anomaly: 5.1463636363636365,
          avgHandlingTime: parseFloat(Math.floor(Math.random() * 55)+'.'+Math.floor(Math.random() * 50)),
          avgResponseTime: parseFloat(Math.floor(Math.random() * 55)+'.'+Math.floor(Math.random() * 50)),
          dateFirstFound: "",
          day: date.format('YYYY-MM-D'),
          month: date.format('YYYY-MM-01'),
          sumAnomaly: 0,
          sumConversations: 33,
          sumHandlingTime: parseFloat(Math.floor(Math.random() * 55)+'.'+Math.floor(Math.random() * 50)),
          sumNegative: 31,
          sumNegativePercent: 88.57,
          sumNeutral: 3,
          sumNeutralPercent: 8.57,
          sumPositive: 1,
          sumPositivePercent: 2.86,
          sumResolved: 5,
          sumResponseTime: parseFloat(Math.floor(Math.random() * 55)+'.'+Math.floor(Math.random() * 50)),
          sumVolume: 100+Math.floor(Math.random() * 1000),
          topicDisplayName: "pay bill",
          trendDate: date.format('YYYY-MM-D'),
          week: "2020-31",
          year: date.format('YYYY-07-01')
        })
      }
      this.chartData.data.trend = trend.reverse();
    }
  },

  created() {
    this.wyChart = new WyChartModule();
  }
});
</script>

<style lang="scss">
.wy-analytics-contact-reasons-detail-topic-anomalies-chart {
  width: 100%;
  height: 276px;
  padding: 10px 0px 0px 0px;
  
  .plot-container.plotly {
    width: 100%;
  }

  .anomalies-chart {
    width: 100%;
  }

  > table {
    width: 100%;
    font-size: 16px;
    font-weight: 500;
  }
  
  table.tr {
    vertical-align: baseline !important;
  }
  .title {
    font-size: 16px;
    font-weight: 500;
    line-height: 0;
    color: #002852;
  }

  .el-select:hover .el-input__inner {
    border-color: var(--site-font-primary-color);
  }
  .el-input.is-focus .el-input__inner {
    border-color: var(--site-font-primary-color);
  }
  .el-select .el-input__inner:focus {
    border-color: var(--site-font-primary-color);
  }
  input.el-input__inner {
    border-width: 1px 1px 1px 1px !important;
    border-radius: 10px;
    padding-left: 13px;
    padding-top: 2px;
    border-color: var(--site-font-primary-color);
  }
  .el-select .el-input .el-select__caret {
    background-image: url('../../assets/icons/icon_dropdown_arrow_down.png');
    background-repeat: no-repeat;
    background-size: 8px 5px;
    background-position-x: 4px;
    background-position-y: 12px;
    width: 20px;
    height: 20px;
    vertical-align: middle;
    transform: rotateZ(0);
  }
  .el-icon-arrow-up:before {
    content: " ";
  }
}
</style>